import crossIcon from '_assets/img/icons/svg/cross-icon.svg';
import crossSuccessIcon from '_assets/img/icons/svg/cross-success.svg';
import Button from '_atoms/Button/Button';
import Tree from '_atoms/Tree';
import { TranslatableString } from '_i18n/no/translations';
import { FC, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './Toast.scss';

export type ToastType = 'success' | 'warning' | 'info';

export type ToastProps = {
    type: ToastType;
    title: TranslatableString;
    content: ReactNode | TranslatableString;
    show: boolean;
    onClose: () => void;
    /** show tree graphic to the right */
    showTree?: boolean;
    autoClose?: boolean;
    autoCloseTime?: number;
    onButtonClick?: () => void;
    buttonLabel?: TranslatableString;
    hasCloseButton?: boolean;
    extraClasses?: string;
};
const Toast: FC<ToastProps> = ({
    type = 'info',
    title,
    content,
    show,
    onClose,
    showTree = false,
    autoClose = true,
    autoCloseTime = 3000, //default 3 seconds
    onButtonClick,
    buttonLabel,
    hasCloseButton = true,
    extraClasses,
}) => {
    const { t } = useTranslation();
    /**
     * If autoClose is true and toast is
     * shown, then we should hide the toast
     * after some time. By default it is
     * 3 seconds
     */
    useEffect(() => {
        if (autoClose && show) {
            setTimeout(() => {
                onClose();
            }, autoCloseTime);
        }
    }, [show]);

    /**
     * It will return the content
     * @returns {ReactNode | TranslatableString} content
     */
    const getContent = (): ReactNode | TranslatableString => {
        if (!content) {
            return <></>;
        }

        if (typeof content === 'string') {
            return t(content as TranslatableString);
        }

        return content;
    };

    const closeIcon = type === 'success' ? crossSuccessIcon : crossIcon;

    const [isVisible, setIsVisible] = useState(false);
    const [shouldRender, setShouldRender] = useState(false);

    useEffect(() => {
        if (show) {
            setShouldRender(true);
            // Small delay to ensure DOM is ready for animation
            setTimeout(() => setIsVisible(true), 10);
        } else {
            setIsVisible(false);
            // Wait for animation to complete before unmounting
            setTimeout(() => setShouldRender(false), 400);
        }
    }, [show]);

    if (!shouldRender) {
        return null;
    }

    return (
        <div
            className={`toast-wrapper toast-${type} ${extraClasses ?? ''} ${
                isVisible ? 'toast-visible' : 'toast-hidden'
            }`}
        >
            <div className={`toast-top ${!title ? 'without-title' : ''}`}>
                {title && <div className="toast-header">{t(title)}</div>}
                {hasCloseButton && (
                    <div className="close-toast" onClick={onClose}>
                        <img src={closeIcon} alt="cross icon" />
                    </div>
                )}
            </div>
            <div className="toast-content">{getContent()}</div>
            {onButtonClick && buttonLabel && (
                <div className="toast-button">
                    <Button onClick={onButtonClick}>{t(buttonLabel)}</Button>
                </div>
            )}
            {showTree && (
                <div className="toast-tree">
                    <Tree />
                </div>
            )}
        </div>
    );
};

export default Toast;
